// src/services/orderDataService.js
// Shared order data service for both PackersToolkit and ProductionToolkit

import { db } from "./firebase";
import { collection, getDocs, query, where, onSnapshot } from "firebase/firestore";
import { parseDDMMYYYY, formatYYYYMMDD, getStartOfWeek, getWeekRange } from "./dateService.js";
import { ensureOrderFolderArchive } from "./orderArchiveService.js";

// Dynamic import of orderDeskService
let orderDeskService = null;
let orderDeskLoading = import('./orderDeskService').then(module => {
    orderDeskService = module.default;
    return orderDeskService;
}).catch(err => {
    console.error('Failed to load OrderDesk service:', err);
    return null;
});

// In-memory cache
const orderCache = {
    data: new Map(), // weekKey -> orders
    timestamps: new Map(), // weekKey -> timestamp
    TTL_MS: 30 * 60 * 1000 // 30 minutes - orders don't change frequently
};

// API call tracking
const apiCallTracker = {
    calls: new Map(), // endpoint -> { count, lastCall, totalCalls }
    debounceTimers: new Map(), // requestKey -> timer
    DEBOUNCE_MS: 1000 // 1 second debounce
};

/**
 * Track API call for monitoring
 */
function trackAPICall(endpoint, requestKey = null) {
    const now = Date.now();
    const key = requestKey || endpoint;
    
    if (!apiCallTracker.calls.has(endpoint)) {
        apiCallTracker.calls.set(endpoint, { count: 0, lastCall: 0, totalCalls: 0 });
    }
    
    const tracker = apiCallTracker.calls.get(endpoint);
    tracker.count++;
    tracker.totalCalls++;
    tracker.lastCall = now;
    
    console.log(`[OrderDesk API] Call #${tracker.totalCalls} to ${endpoint} (${tracker.count} calls in last 5 minutes)`);
    
    // Log warning if too many calls
    if (tracker.count > 10) {
        console.warn(`[OrderDesk API] High frequency detected: ${tracker.count} calls to ${endpoint} in last 5 minutes`);
    }
}

/**
 * Debounce API calls to prevent rapid successive requests
 */
function debounceAPICall(requestKey, apiCallFn, delay = apiCallTracker.DEBOUNCE_MS) {
    return new Promise((resolve, reject) => {
        // Clear existing timer
        if (apiCallTracker.debounceTimers.has(requestKey)) {
            clearTimeout(apiCallTracker.debounceTimers.get(requestKey));
        }
        
        // Set new timer
        const timer = setTimeout(async () => {
            apiCallTracker.debounceTimers.delete(requestKey);
            try {
                const result = await apiCallFn();
                resolve(result);
            } catch (error) {
                reject(error);
            }
        }, delay);
        
        apiCallTracker.debounceTimers.set(requestKey, timer);
    });
}

/**
 * Get API usage statistics for monitoring
 */
export function getAPIUsageStats() {
    const stats = {
        totalCalls: 0,
        endpoints: {},
        warnings: []
    };
    
    for (const [endpoint, data] of apiCallTracker.calls.entries()) {
        stats.totalCalls += data.totalCalls;
        stats.endpoints[endpoint] = {
            totalCalls: data.totalCalls,
            recentCalls: data.count,
            lastCall: new Date(data.lastCall).toISOString()
        };
        
        if (data.count > 10) {
            stats.warnings.push(`High frequency: ${data.count} calls to ${endpoint} in last 5 minutes`);
        }
    }
    
    return stats;
}

/**
 * Clear API usage statistics (for testing/reset)
 */
export function clearAPIUsageStats() {
    apiCallTracker.calls.clear();
    console.log('[OrderDesk API] Usage statistics cleared');
}

// Date utility functions are now imported from dateUtils.js

/**
 * Check if cache is valid for a given week
 */
function isCacheValid(weekKey) {
    if (!orderCache.data.has(weekKey)) return false;
    
    const timestamp = orderCache.timestamps.get(weekKey);
    if (!timestamp) return false;
    
    const age = Date.now() - timestamp;
    return age < orderCache.TTL_MS;
}

/**
 * Get delivery dates within 3 weeks from today
 */
function getRelevantDeliveryDates() {
    const today = new Date();
    const threeWeeksAgo = new Date(today);
    threeWeeksAgo.setDate(today.getDate() - 21); // 3 weeks ago
    
    const dates = [];
    for (let i = 0; i < 28; i++) { // Check 4 weeks ahead
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        dates.push(date.toISOString().slice(0, 10)); // YYYY-MM-DD format
    }
    
    return dates;
}

/**
 * Fetch orders for a specific week from OrderDesk (with caching and delivery date optimization)
 */
export async function fetchOrdersForWeek(weekStart, options = {}) {
    const { forceRefresh = false, limit = 200, customerFilters = {} } = options;
    
    const weekKey = `${weekStart}_${JSON.stringify(customerFilters)}`;
    
    // Check cache
    if (!forceRefresh && isCacheValid(weekKey)) {
        console.log(`Using cached orders for week ${weekKey}`);
        return orderCache.data.get(weekKey) || [];
    }
    
    try {
        // Wait for OrderDesk service to load
        if (!orderDeskService) {
            await orderDeskLoading;
        }
        
        if (!orderDeskService) {
            throw new Error("OrderDesk service not available");
        }
        
        // Get relevant delivery dates (within 3 weeks)
        const relevantDates = getRelevantDeliveryDates();
        const { start, end } = getWeekRange(weekStart);
        
        // Filter to only dates within our week range
        const weekDates = relevantDates.filter(date => {
            const dateMs = new Date(date).getTime();
            const startMs = new Date(start).getTime();
            const endMs = new Date(end).getTime();
            return dateMs >= startMs && dateMs <= endMs;
        });
        
        console.log(`Fetching orders for delivery dates: ${weekDates.join(', ')}`);
        
        // Fetch orders from delivery date folders
        const allOrders = [];
        for (const deliveryDate of weekDates) {
            try {
                // Fetch from folder named by delivery date (e.g., "2025-01-15")
                const rawOrders = await orderDeskService.fetchOrders(deliveryDate, limit);
                allOrders.push(...rawOrders);
            } catch (error) {
                console.warn(`Failed to fetch orders for delivery date ${deliveryDate}:`, error.message);
                // Continue with other dates
            }
        }
        
        // If no delivery date folders found, fall back to week-based folder
        if (allOrders.length === 0) {
            console.log(`No delivery date folders found, falling back to week folder: ${weekStart}`);
            const rawOrders = await orderDeskService.fetchOrders(weekStart, limit);
            allOrders.push(...rawOrders);
        }

        // Group by order ID (handles line-by-line format)
        let groupedOrders = orderDeskService.groupOrdersByOrderId(allOrders) || [];
        
        // Fix date formats in grouped orders
        groupedOrders = groupedOrders.map(order => {
            // Fix delivery date format
            if (order.deliveryDate) {
                const parsedDate = parseDDMMYYYY(order.deliveryDate);
                if (parsedDate) {
                    order.deliveryDate = formatYYYYMMDD(parsedDate);
                }
            }
            
            // Fix dispatch date format
            if (order.dispatchDate) {
                const parsedDate = parseDDMMYYYY(order.dispatchDate);
                if (parsedDate) {
                    order.dispatchDate = formatYYYYMMDD(parsedDate);
                }
            }
            
            return order;
        });

        // Apply customer filters before processing
        if (customerFilters.searchText) {
            const searchLower = customerFilters.searchText.toLowerCase();
            groupedOrders = groupedOrders.filter(o => 
                (o.customerFirstName || '').toLowerCase().includes(searchLower) ||
                (o.customerLastName || '').toLowerCase().includes(searchLower) ||
                (o.customerCompany || '').toLowerCase().includes(searchLower) ||
                (o.orderId || '').toLowerCase().includes(searchLower) ||
                (o.email || '').toLowerCase().includes(searchLower)
            );
        }
        
        if (customerFilters.skuInclude) {
            const skuLower = customerFilters.skuInclude.toLowerCase();
            groupedOrders = groupedOrders.filter(o => 
                (o.items || []).some(item => 
                    (item.sku || '').toLowerCase().includes(skuLower)
                )
            );
        }
        
        if (customerFilters.skuExclude) {
            const skuLower = customerFilters.skuExclude.toLowerCase();
            groupedOrders = groupedOrders.filter(o => 
                !(o.items || []).some(item => 
                    (item.sku || '').toLowerCase().includes(skuLower)
                )
            );
        }

        // Strict week filtering: Monday 00:00 → Friday 23:59 (local ISO)
        const startMs = new Date(start).getTime();
        const endMs = new Date(end).getTime();

        groupedOrders = groupedOrders.filter((o) => {
            const whenIso = o.deliveryDate || o.dispatchDate || o.createdAt || o.updatedAt;
            if (!whenIso) return true; // keep if not provided
            
            // Parse the date using our centralized utility
            const date = parseDDMMYYYY(whenIso) || new Date(whenIso);
            if (!date || isNaN(date.getTime())) return true; // keep if invalid
            
            const ms = date.getTime();
            return ms >= startMs && ms <= endMs;
        });

        // Deduplicate by orderId as a safety (some folders may cross list)
        const seen = new Set();
        groupedOrders = groupedOrders.filter((o) => {
            const id = (o.orderId || o["Order ID"] || "").toString().trim();
            if (!id) return false;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });

        // Cache the result
        orderCache.data.set(weekKey, groupedOrders);
        orderCache.timestamps.set(weekKey, Date.now());
        
        console.log(`Fetched and cached ${groupedOrders.length} orders for week ${weekKey} (from ${weekDates.length} delivery date folders)`);
        
        return groupedOrders;
    } catch (error) {
        console.error(`Failed to fetch orders for week ${weekStart}:`, error);
        throw error;
    }
}

/**
 * Fetch orders for multiple weeks
 */
export async function fetchOrdersForWeeks(weekStarts = [], options = {}) {
    const allOrders = [];
    
    for (const weekStart of weekStarts) {
        try {
            const orders = await fetchOrdersForWeek(weekStart, options);
            allOrders.push(...orders);
        } catch (error) {
            console.error(`Failed to fetch orders for week ${weekStart}:`, error);
            // Continue with other weeks
        }
    }
    
    return allOrders;
}

/**
 * Organize orders by dispatch date (user-set in OrderDesk checkout_data)
 * Returns: { dispatchDateMap, sortedDates, totalOrders, dateCount }
 */
export function organizeOrdersByDispatchDate(orders) {
    const dispatchDateMap = new Map();

    orders.forEach(order => {
        const dispatchDate = order.dispatchDate || order.deliveryDate;
        if (!dispatchDate) {
            const noDateKey = 'no-dispatch-date';
            if (!dispatchDateMap.has(noDateKey)) {
                dispatchDateMap.set(noDateKey, []);
            }
            dispatchDateMap.get(noDateKey).push(order);
            return;
        }

        const parsedDate = parseDDMMYYYY(dispatchDate);
        const normalizedDate = parsedDate ? formatYYYYMMDD(parsedDate) : dispatchDate;

        if (!dispatchDateMap.has(normalizedDate)) {
            dispatchDateMap.set(normalizedDate, []);
        }
        dispatchDateMap.get(normalizedDate).push(order);
    });

    const sortedDates = Array.from(dispatchDateMap.keys())
        .filter(date => date !== 'no-dispatch-date')
        .sort();

    if (dispatchDateMap.has('no-dispatch-date')) {
        sortedDates.push('no-dispatch-date');
    }

    return {
        dispatchDateMap,
        sortedDates,
        totalOrders: orders.length,
        dateCount: dispatchDateMap.size
    };
}

/**
 * Organize orders by delivery date
 * Returns: Map<deliveryDate, orders[]>
 */
export function organizeOrdersByDeliveryDate(orders) {
    const deliveryDateMap = new Map();
    
    orders.forEach(order => {
        const deliveryDate = order.deliveryDate || order.dispatchDate;
        if (!deliveryDate) {
            // Put orders without delivery date in a special category
            const noDateKey = 'no-delivery-date';
            if (!deliveryDateMap.has(noDateKey)) {
                deliveryDateMap.set(noDateKey, []);
            }
            deliveryDateMap.get(noDateKey).push(order);
            return;
        }
        
        // Parse and normalize delivery date using centralized utility
        const parsedDate = parseDDMMYYYY(deliveryDate);
        const normalizedDate = parsedDate ? formatYYYYMMDD(parsedDate) : deliveryDate;
        
        if (!deliveryDateMap.has(normalizedDate)) {
            deliveryDateMap.set(normalizedDate, []);
        }
        deliveryDateMap.get(normalizedDate).push(order);
    });
    
    // Sort delivery dates
    const sortedDates = Array.from(deliveryDateMap.keys())
        .filter(date => date !== 'no-delivery-date')
        .sort();
    
    if (deliveryDateMap.has('no-delivery-date')) {
        sortedDates.push('no-delivery-date');
    }
    
    return {
        deliveryDateMap,
        sortedDates,
        totalOrders: orders.length,
        dateCount: deliveryDateMap.size
    };
}

/** Normalize ID for matching (strip # and leading --) */
const normalizeIdForSet = (v) => (v ?? '').toString().trim().replace(/^#+/, '').replace(/^--+/, '');

/**
 * Build a Set that matches aggregateSkuRequirements / PackersToolkit: raw + normalized ids.
 * @param {Set|Iterable} idSet
 * @returns {Set<string>}
 */
export function buildNormalizedOrderIdSet(idSet) {
    const out = new Set();
    if (idSet && typeof idSet.forEach === 'function') {
        idSet.forEach((id) => {
            const n = normalizeIdForSet(id);
            if (n) out.add(n);
            const raw = (id ?? '').toString().trim();
            if (raw) out.add(raw);
        });
    }
    return out;
}

/**
 * Order id variants for Firestore packedOrders / shippedOrders matching (same rules as aggregateSkuRequirements).
 * @param {object} order
 * @returns {string[]}
 */
export function getOrderIdVariantsForMatch(order) {
    const ids = [
        order?.orderId,
        order?.id,
        order?.order_id,
        order?.order_number,
        order?.orderNumber,
        order?.source_id,
        order?.sourceId,
    ]
        .map((v) => normalizeIdForSet(v))
        .filter(Boolean);
    const seen = new Set();
    return ids.filter((id) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
    });
}

/**
 * True if any order id variant is in the normalized id set (packed or shipped doc ids).
 * @param {object} order
 * @param {Set<string>} normalizedIdSet from buildNormalizedOrderIdSet
 */
export function isOrderInNormalizedIdSet(order, normalizedIdSet) {
    const variants = getOrderIdVariantsForMatch(order);
    return variants.some((id) => normalizedIdSet.has(id));
}

/**
 * Remaining production/packing demand: exclude orders already packed or shipped (same as planner).
 * @param {{ ignorePacked?: boolean, ignoreShipped?: boolean }} [options]
 *   - `ignorePacked`: do not exclude packed orders (gross prepack demand).
 *   - `ignoreShipped`: do not exclude shipped orders (gross pre-shipped demand). Usually both are true together for full “as if nothing left the building” totals.
 */
export function orderExcludedFromRemainingDemand(order, packedNorm, shippedNorm, options = {}) {
    const ignorePacked = options?.ignorePacked === true;
    const ignoreShipped = options?.ignoreShipped === true;
    if (!ignoreShipped && isOrderInNormalizedIdSet(order, shippedNorm)) return { excluded: true, reason: 'shipped' };
    if (!ignorePacked && isOrderInNormalizedIdSet(order, packedNorm)) return { excluded: true, reason: 'packed' };
    return { excluded: false };
}

/**
 * Get packed order IDs from Firestore (includes normalized variants for reliable matching)
 */
export async function getPackedOrderIds() {
    try {
        const snap = await getDocs(collection(db, "packedOrders"));
        const set = new Set();
        snap.docs.forEach((d) => {
            set.add(d.id);
            const n = normalizeIdForSet(d.id);
            if (n) set.add(n);
        });
        return set;
    } catch (error) {
        console.error("Failed to get packed order IDs:", error);
        return new Set();
    }
}

/**
 * Get shipped order IDs from Firestore (includes normalized variants for reliable matching)
 */
export async function getShippedOrderIds() {
    try {
        const snap = await getDocs(collection(db, "shippedOrders"));
        const set = new Set();
        snap.docs.forEach((d) => {
            set.add(d.id);
            const n = normalizeIdForSet(d.id);
            if (n) set.add(n);
        });
        return set;
    } catch (error) {
        console.error("Failed to get shipped order IDs:", error);
        return new Set();
    }
}

/**
 * Get orders with their packing status
 * Returns: { orders, packedIds, shippedIds }
 */
export async function getOrdersWithStatus(weekStart) {
    try {
        const [orders, packedIds, shippedIds] = await Promise.all([
            fetchOrdersForWeek(weekStart),
            getPackedOrderIds(),
            getShippedOrderIds()
        ]);
        
        return {
            orders,
            packedIds,
            shippedIds
        };
    } catch (error) {
        console.error("Failed to get orders with status:", error);
        throw error;
    }
}

export async function archiveOrdersForFolder(folderContext = {}, orders = [], options = {}) {
    return ensureOrderFolderArchive(folderContext, orders, options);
}

/**
 * Calculate SKU requirements from orders
 * Returns: Map<sku, { total, packed, shipped, unpacked, orders: [...] }>
 * 
 * CRITICAL: Expands box products to show their component requirements
 * e.g., If order has 1x BX-CHAR-0800-BOX, the results will include:
 *   - BX-CHAR-0800-BOX: 1 unit (the box itself)
 *   - CH-NDSM-0080-SLC: 1 unit (component inside box)
 *   - CH-COPP-0050-SLC: 1 unit (component inside box)
 *   - etc. for all components
 * 
 * Now also tracks shipped status for progressive fulfillment:
 * - packed: Orders packed but not shipped (still count toward needs)
 * - shipped: Orders fully shipped (removed from needs)
 */
export async function aggregateSkuRequirements(orders, packedIds = new Set(), shippedIds = new Set()) {
    const skuMap = new Map();
    
    // Import unifiedProductService dynamically to avoid circular dependencies
    const { default: unifiedProductService } = await import('./unifiedProductService');
    
    console.log(`[aggregateSkuRequirements] Processing ${orders.length} orders`);

    const packedNorm = buildNormalizedOrderIdSet(packedIds);
    const shippedNorm = buildNormalizedOrderIdSet(shippedIds);

    for (let index = 0; index < orders.length; index++) {
        const order = orders[index];
        const orderId = order.orderId || order.id;
        const isPacked = isOrderInNormalizedIdSet(order, packedNorm);
        const isShipped = isOrderInNormalizedIdSet(order, shippedNorm);
        
        // Handle both structures: order.items array OR direct SKU/quantity on order
        // CRITICAL: Prefer originalItems (raw, unexpanded) when available - PackersToolkit pre-expands
        // order.items for display, so using order.items would cause double expansion and 2x component counts.
        // Production Planning uses raw items and expands once; we must do the same here.
        let items = [];
        
        if (order.originalItems && Array.isArray(order.originalItems) && order.originalItems.length > 0) {
            items = order.originalItems;
            if (index === 0) console.log(`[aggregateSkuRequirements] Using order.originalItems (raw) for first order:`, items.slice(0, 2));
        } else if (order.items && Array.isArray(order.items) && order.items.length > 0) {
            // Standard structure: order.items array (may already be expanded by caller - use with caution)
            // WARNING: If order.items is already expanded, this will cause double expansion
            // Check if items appear to be already expanded (contain box component SKUs)
            const mightBeExpanded = order.items.some(item => {
                const sku = (item.sku || '').toString();
                // If we see component SKUs that are typically inside boxes, items might be expanded
                // This is a heuristic check - not perfect but helps detect issues
                return sku.includes('-') && sku.length > 8; // Component SKUs often have dashes and are longer
            });
            if (mightBeExpanded && index === 0) {
                console.warn(`[aggregateSkuRequirements] WARNING: order.items for first order may already be expanded. Using originalItems is preferred.`, {
                    hasOriginalItems: !!order.originalItems,
                    itemsLength: order.items.length,
                    sampleItems: order.items.slice(0, 3)
                });
            }
            items = order.items;
            if (index === 0) console.log(`[aggregateSkuRequirements] Using order.items array for first order:`, items.slice(0, 2));
        } else if (order.order_items && Array.isArray(order.order_items)) {
            // OrderDesk structure: order_items array
            items = order.order_items.map(item => ({
                sku: item.product_code || item.sku || item.product_sku || item.product_id,
                quantity: item.quantity || item.qty || item.amount
            }));
            if (index === 0) console.log(`[aggregateSkuRequirements] Using order.order_items array for first order:`, items.slice(0, 2));
        } else if (order.sku && order.quantity) {
            // OrderDesker structure: SKU and quantity directly on order
            items = [{ sku: order.sku, quantity: order.quantity }];
            if (index === 0) console.log(`[aggregateSkuRequirements] Using direct sku/quantity for first order:`, items);
        } else {
            if (index === 0) console.log(`[aggregateSkuRequirements] No recognizable structure found for first order:`, {
                hasItems: !!order.items,
                hasOrderItems: !!order.order_items,
                hasSku: !!order.sku,
                hasQuantity: !!order.quantity,
                orderKeys: Object.keys(order)
            });
        }
        
        // CRITICAL: Expand boxes into their components before aggregating
        // This ensures reports show both the box AND what's needed to pack it
        const expandedItems = await unifiedProductService.expandBoxes(items);
        
        if (index === 0 && items.length !== expandedItems.length) {
            console.log(`[aggregateSkuRequirements] Box expansion for first order:`, {
                originalItems: items.length,
                expandedItems: expandedItems.length,
                sample: expandedItems.slice(0, 5).map(i => `${i.sku} x${i.quantity}`)
            });
        }
        
        expandedItems.forEach((item, itemIndex) => {
            const sku = item.sku;
            // Match Production Planner (parseFloat) — do not truncate fractional qty with parseInt
            const quantity = parseFloat(item.quantity) || 0;
            
            if (index === 0 && itemIndex === 0) {
                console.log(`[aggregateSkuRequirements] Processing first item:`, {
                    item,
                    sku,
                    quantity,
                    skuValid: !!sku,
                    quantityValid: quantity > 0
                });
            }
            
            if (!sku || quantity <= 0) {
                if (index === 0 && itemIndex === 0) {
                    console.log(`[aggregateSkuRequirements] Skipping item - invalid sku or quantity:`, { sku, quantity });
                }
                return;
            }
            
            if (!skuMap.has(sku)) {
                skuMap.set(sku, {
                    sku,
                    total: 0,
                    packed: 0,
                    shipped: 0,
                    unpacked: 0,
                    orders: []
                });
            }
            
            const entry = skuMap.get(sku);
            entry.total += quantity;
            
            if (isShipped) {
                entry.shipped += quantity;
            } else if (isPacked) {
                entry.packed += quantity;
            } else {
                entry.unpacked += quantity;
            }
            
            // Store order association (deduplicate by orderId per SKU)
            if (!entry.orders.find(o => o.orderId === orderId)) {
                entry.orders.push({
                    orderId,
                    orderNumber: order.orderNumber || order.sourceId || order.source_id || orderId,
                    quantity: 0, // Will accumulate below
                    isPacked,
                    isShipped,
                    customerName: (order.customerName || `${order.customerFirstName || order.customer?.firstName || ''} ${order.customerLastName || order.customer?.lastName || ''}`.trim()) || '—',
                    deliveryDate: order.deliveryDate || order.delivery_date,
                    dispatchDate: order.dispatchDate || order.dispatch_date
                });
            }
            
            // Update quantity for this order
            const orderEntry = entry.orders.find(o => o.orderId === orderId);
            if (orderEntry) {
                orderEntry.quantity += quantity;
            }
        });
    }
    
    console.log(`[aggregateSkuRequirements] Final result:`, {
        totalSKUs: skuMap.size,
        skuList: Array.from(skuMap.keys()),
        sampleEntries: Array.from(skuMap.entries()).slice(0, 3).map(([sku, data]) => ({
            sku,
            total: data.total,
            unpacked: data.unpacked,
            orderCount: data.orders.length
        }))
    });
    
    return skuMap;
}

/**
 * Get production requirements for current week (unpacked orders only)
 * Returns: [{ sku, quantityNeeded, orders: [...] }]
 */
export async function getProductionRequirements(weekStart) {
    try {
        const { orders, packedIds, shippedIds } = await getOrdersWithStatus(weekStart);

        const packedNorm = buildNormalizedOrderIdSet(packedIds);
        const shippedNorm = buildNormalizedOrderIdSet(shippedIds || new Set());
        const unpackedOrders = orders.filter(
            (o) =>
                !isOrderInNormalizedIdSet(o, packedNorm) && !isOrderInNormalizedIdSet(o, shippedNorm)
        );

        const skuRequirements = await aggregateSkuRequirements(unpackedOrders, new Set(), new Set());
        
        return Array.from(skuRequirements.values())
            .map(entry => ({
                sku: entry.sku,
                quantityNeeded: entry.unpacked || entry.total,
                orders: entry.orders
            }))
            .sort((a, b) => b.quantityNeeded - a.quantityNeeded); // Sort by quantity desc
    } catch (error) {
        console.error("Failed to get production requirements:", error);
        throw error;
    }
}

/**
 * Get multi-week forecast
 * Returns: [{ week, requirements: Map<sku, quantity> }]
 */
export async function getMultiWeekForecast(startWeek, weeksAhead = 4) {
    try {
        const weekStarts = [];
        const current = new Date(startWeek);
        
        for (let i = 0; i < weeksAhead; i++) {
            const weekStart = getStartOfWeek(current.toISOString().slice(0, 10));
            if (weekStart) weekStarts.push(weekStart);
            current.setDate(current.getDate() + 7);
        }
        
        const results = [];
        
        for (const week of weekStarts) {
            try {
                const orders = await fetchOrdersForWeek(week);
                const skuReqs = await aggregateSkuRequirements(orders, new Set(), new Set());
                
                results.push({
                    week,
                    weekRange: getWeekRange(week),
                    orderCount: orders.length,
                    requirements: skuReqs
                });
            } catch (error) {
                console.error(`Failed to get forecast for week ${week}:`, error);
                results.push({
                    week,
                    weekRange: getWeekRange(week),
                    orderCount: 0,
                    requirements: new Map(),
                    error: error.message
                });
            }
        }
        
        return results;
    } catch (error) {
        console.error("Failed to get multi-week forecast:", error);
        throw error;
    }
}

/**
 * Clear cache (useful for manual refresh)
 */
export function clearOrderCache() {
    orderCache.data.clear();
    orderCache.timestamps.clear();
    console.log("Order cache cleared");
}

/**
 * Listen to packing status changes (real-time updates)
 */
export function listenToPackingStatus(callback) {
    const unsubPacked = onSnapshot(collection(db, "packedOrders"), () => {
        callback({ type: 'packed' });
    });
    
    const unsubShipped = onSnapshot(collection(db, "shippedOrders"), () => {
        callback({ type: 'shipped' });
    });
    
    return () => {
        unsubPacked();
        unsubShipped();
    };
}

/**
 * Get smart delivery date options for the dropdown
 */
export async function getAvailableDeliveryDates() {
    try {
        console.log('[OrderDataService] Getting smart delivery date options...');
        
        // Wait for OrderDesk service to load
        if (!orderDeskService) {
            await orderDeskLoading;
        }
        
        if (!orderDeskService) {
            throw new Error("OrderDesk service not available");
        }
        
        // Get today and calculate smart date range
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        
        // Create smart date options: yesterday, today, next 14 days
        const smartDates = [];
        
        // Add yesterday (for late orders)
        smartDates.push(formatYYYYMMDD(yesterday));
        
        // Add today through next 14 days
        for (let i = 0; i <= 14; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            smartDates.push(formatYYYYMMDD(date));
        }
        
        console.log(`[OrderDataService] Generated ${smartDates.length} smart date options`);
        
        // Use date filtering to efficiently check which dates have orders
        const datesWithOrders = [];
        
        // Check each date for orders using optimized date filtering
        for (const dateStr of smartDates) {
            try {
                const targetDate = new Date(dateStr);
                
                // Use tight date range for this specific date
                const startDate = new Date(targetDate);
                startDate.setHours(0, 0, 0, 0);
                
                const endDate = new Date(targetDate);
                endDate.setHours(23, 59, 59, 999);
                
                const dateFilterOptions = {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                };
                
                // Quick check with small limit to see if any orders exist
                const rawOrders = await orderDeskService.fetchOrders("All Orders", 5, dateFilterOptions);
                
                if (rawOrders && rawOrders.length > 0) {
                    // Group and check if any orders actually match this delivery date
                    const groupedOrders = orderDeskService.groupOrdersByOrderId(rawOrders) || [];
                    const hasMatchingOrders = groupedOrders.some(order => {
                        const orderDeliveryDate = order.deliveryDate;
                        if (orderDeliveryDate) {
                            const parsedDate = parseDDMMYYYY(orderDeliveryDate);
                            if (parsedDate) {
                                const normalizedDate = formatYYYYMMDD(parsedDate);
                                return normalizedDate === dateStr;
                            }
                        }
                        return false;
                    });
                    
                    if (hasMatchingOrders) {
                        datesWithOrders.push(dateStr);
                        console.log(`[OrderDataService] Found orders for ${dateStr}`);
                    }
                }
            } catch (error) {
                console.warn(`[OrderDataService] Failed to check orders for ${dateStr}:`, error.message);
                // Don't fail completely, just skip this date
            }
        }
        
        // If no dates found with the smart approach, fall back to basic date list
        if (datesWithOrders.length === 0) {
            console.log('[OrderDataService] No orders found in smart dates, providing basic date options');
            return smartDates.slice(0, 7); // Return first week as fallback
        }
        
        console.log(`[OrderDataService] Found ${datesWithOrders.length} dates with orders:`, datesWithOrders);
        
        // Sort dates (today first, then future dates, then past dates)
        const todayStr = formatYYYYMMDD(today);
        const sortedDates = datesWithOrders.sort((a, b) => {
            if (a === todayStr) return -1;
            if (b === todayStr) return 1;
            
            const dateA = new Date(a);
            const dateB = new Date(b);
            
            // Future dates first (ascending), then past dates (descending)
            if (dateA >= today && dateB >= today) {
                return dateA.getTime() - dateB.getTime();
            } else if (dateA < today && dateB < today) {
                return dateB.getTime() - dateA.getTime();
            } else if (dateA >= today) {
                return -1;
            } else {
                return 1;
            }
        });
        
        return sortedDates;
    } catch (error) {
        console.error('Failed to get available delivery dates:', error);
        
        // Fallback: provide basic date options
        const today = new Date();
        const fallbackDates = [];
        for (let i = -1; i <= 7; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            fallbackDates.push(formatYYYYMMDD(date));
        }
        return fallbackDates;
    }
}

/**
 * Fetch orders for a specific delivery date (EXACT match only)
 */
export async function fetchOrdersForDeliveryDate(deliveryDate, options = {}) {
    const { forceRefresh = false, limit = 200, customerFilters = {} } = options;
    
    const dateKey = `${deliveryDate}_${JSON.stringify(customerFilters)}`;
    
    // Check cache first
    if (!forceRefresh && isCacheValid(dateKey)) {
        console.log(`[Cache Hit] Using cached orders for delivery date ${dateKey}`);
        return orderCache.data.get(dateKey) || [];
    }
    
    // Track API call
    trackAPICall(`fetchOrdersForDeliveryDate(${deliveryDate})`, dateKey);
    
    console.log(`[API Call] Fetching orders for delivery date ${deliveryDate} (cache miss or force refresh)`);
    
    try {
        // Wait for OrderDesk service to load
        if (!orderDeskService) {
            await orderDeskLoading;
        }
        
        if (!orderDeskService) {
            throw new Error("OrderDesk service not available");
        }
        
        const allOrders = [];
        
        // Parse the target delivery date
        const targetDate = parseDDMMYYYY(deliveryDate) || new Date(deliveryDate);
        if (!targetDate || isNaN(targetDate.getTime())) {
            throw new Error(`Invalid delivery date: ${deliveryDate}`);
        }
        
        console.log(`Fetching orders for EXACT delivery date: ${deliveryDate}`);
        
        // OPTIMIZED: Use date filtering to reduce data transfer
        // Calculate date range: target date ± 1 day to account for timezone differences
        const startDate = new Date(targetDate);
        startDate.setDate(targetDate.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        
        const endDate = new Date(targetDate);
        endDate.setDate(targetDate.getDate() + 1);
        endDate.setHours(23, 59, 59, 999);
        
        const dateFilterOptions = {
            startDate: startDate.toISOString(), // YYYY-MM-DDTHH:MM:SSZ
            endDate: endDate.toISOString(),
            modifiedStartDate: startDate.toISOString(), // Also check recently modified orders
        };
        
        console.log(`[OrderDataService] Using date filters:`, {
            startDate: dateFilterOptions.startDate,
            endDate: dateFilterOptions.endDate,
            targetDeliveryDate: deliveryDate
        });
        
        try {
            // Fetch with date filtering - much more efficient!
            const rawOrders = await orderDeskService.fetchOrders(deliveryDate, limit, dateFilterOptions);
            console.log(`Found ${rawOrders.length} orders with date filtering (was fetching ALL orders before)`);
            
            // Debug: Show sample of raw orders
            if (rawOrders.length > 0) {
                console.log(`[DEBUG] Sample filtered orders:`, rawOrders.slice(0, 2).map(order => ({
                    orderId: order.orderId || order["Order ID"],
                    deliveryDate: order.deliveryDate,
                    dispatchDate: order.dispatchDate,
                    customer: order.customerFirstName || order.customerLastName || order.customerCompany
                })));
            }
            
            allOrders.push(...rawOrders);
        } catch (error) {
            console.warn(`Failed to fetch orders for delivery date ${deliveryDate}:`, error.message);
            
            // Fallback: try without date filtering if the API doesn't support it
            console.log(`[OrderDataService] Falling back to folder-only fetch...`);
            try {
                const fallbackOrders = await orderDeskService.fetchOrders(deliveryDate, limit);
                console.log(`Fallback fetch returned ${fallbackOrders.length} orders`);
                allOrders.push(...fallbackOrders);
            } catch (fallbackError) {
                console.error(`Fallback fetch also failed:`, fallbackError);
                throw fallbackError;
            }
        }
        
        console.log(`Total raw orders fetched: ${allOrders.length}`);
        
        // Group by order ID
        let groupedOrders = orderDeskService.groupOrdersByOrderId(allOrders) || [];
        
        // Fix date formats in grouped orders
        groupedOrders = groupedOrders.map(order => {
            if (order.deliveryDate) {
                const parsedDate = parseDDMMYYYY(order.deliveryDate);
                if (parsedDate) {
                    order.deliveryDate = formatYYYYMMDD(parsedDate);
                }
            }
            
            if (order.dispatchDate) {
                const parsedDate = parseDDMMYYYY(order.dispatchDate);
                if (parsedDate) {
                    order.dispatchDate = formatYYYYMMDD(parsedDate);
                }
            }
            
            return order;
        });
        
        // Filter to only orders that match the EXACT delivery date (no 4-day horizon)
        const targetDateStr = formatYYYYMMDD(targetDate);
        console.log(`Filtering orders for EXACT delivery date: ${targetDateStr}`);
        console.log(`Total orders before filtering: ${groupedOrders.length}`);
        
        groupedOrders = groupedOrders.filter(order => {
            const orderDeliveryDate = order.deliveryDate;
            
            // Debug logging for first few orders
            if (groupedOrders.indexOf(order) < 5) {
                console.log(`[DEBUG] Order ${order.orderId}: delivery=${orderDeliveryDate}, target=${targetDateStr}`);
            }
            
            // Include ONLY if delivery date matches exactly
            const isExactMatch = orderDeliveryDate === targetDateStr;
            
            if (isExactMatch && groupedOrders.indexOf(order) < 5) {
                console.log(`[DEBUG] Order ${order.orderId} INCLUDED: exact delivery date match`);
            } else if (!isExactMatch && groupedOrders.indexOf(order) < 5) {
                console.log(`[DEBUG] Order ${order.orderId} EXCLUDED: delivery date mismatch (${orderDeliveryDate} ≠ ${targetDateStr})`);
            }
            
            return isExactMatch;
        });
        
        console.log(`Total orders after filtering: ${groupedOrders.length}`);
        
        // Additional safety filter: exclude orders with dates more than 30 days in the past
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        // Also exclude orders from previous years (before 2025)
        const year2025 = new Date('2025-01-01');
        
        groupedOrders = groupedOrders.filter(order => {
            const orderDeliveryDate = order.deliveryDate;
            const orderDispatchDate = order.dispatchDate;
            
            // Check delivery date
            if (orderDeliveryDate) {
                const deliveryDate = parseDDMMYYYY(orderDeliveryDate);
                if (deliveryDate) {
                    if (deliveryDate < thirtyDaysAgo || deliveryDate < year2025) {
                        console.log(`Excluding order ${order.orderId} with delivery date ${orderDeliveryDate} (too old)`);
                        return false;
                    }
                }
            }
            
            // Check dispatch date
            if (orderDispatchDate) {
                const dispatchDate = parseDDMMYYYY(orderDispatchDate);
                if (dispatchDate) {
                    if (dispatchDate < thirtyDaysAgo || dispatchDate < year2025) {
                        console.log(`Excluding order ${order.orderId} with dispatch date ${orderDispatchDate} (too old)`);
                        return false;
                    }
                }
            }
            
            return true;
        });
        
        console.log(`Total orders after 30-day filter: ${groupedOrders.length}`);
        
        // Debug: Show sample of orders that passed filtering
        if (groupedOrders.length > 0) {
            console.log(`[DEBUG] Sample of filtered orders:`, groupedOrders.slice(0, 3).map(order => ({
                orderId: order.orderId,
                deliveryDate: order.deliveryDate,
                dispatchDate: order.dispatchDate,
                customer: order.customerFirstName || order.customerLastName || order.customerCompany
            })));
        }
        
        // Apply customer filters
        if (customerFilters.searchText) {
            const searchLower = customerFilters.searchText.toLowerCase();
            groupedOrders = groupedOrders.filter(o => 
                (o.customerFirstName || '').toLowerCase().includes(searchLower) ||
                (o.customerLastName || '').toLowerCase().includes(searchLower) ||
                (o.customerCompany || '').toLowerCase().includes(searchLower) ||
                (o.orderId || '').toLowerCase().includes(searchLower) ||
                (o.email || '').toLowerCase().includes(searchLower)
            );
        }
        
        if (customerFilters.skuInclude) {
            const skuLower = customerFilters.skuInclude.toLowerCase();
            groupedOrders = groupedOrders.filter(o => 
                (o.items || []).some(item => 
                    (item.sku || '').toLowerCase().includes(skuLower)
                )
            );
        }
        
        if (customerFilters.skuExclude) {
            const skuLower = customerFilters.skuExclude.toLowerCase();
            groupedOrders = groupedOrders.filter(o => 
                !(o.items || []).some(item => 
                    (item.sku || '').toLowerCase().includes(skuLower)
                )
            );
        }
        
        // Deduplicate by orderId
        const seen = new Set();
        groupedOrders = groupedOrders.filter((o) => {
            const id = (o.orderId || o["Order ID"] || "").toString().trim();
            if (!id) return false;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });
        
        // Cache the result
        orderCache.data.set(dateKey, groupedOrders);
        orderCache.timestamps.set(dateKey, Date.now());
        
        console.log(`Fetched and cached ${groupedOrders.length} orders for delivery date ${deliveryDate}`);
        
        return groupedOrders;
    } catch (error) {
        console.error(`Failed to fetch orders for delivery date ${deliveryDate}:`, error);
        throw error;
    }
}

export default {
    fetchOrdersForWeek,
    fetchOrdersForWeeks,
    getPackedOrderIds,
    getShippedOrderIds,
    getOrdersWithStatus,
    aggregateSkuRequirements,
    getProductionRequirements,
    getMultiWeekForecast,
    clearOrderCache,
    listenToPackingStatus,
    getStartOfWeek,
    getWeekRange,
    organizeOrdersByDeliveryDate,
    getRelevantDeliveryDates,
    getAvailableDeliveryDates,
    fetchOrdersForDeliveryDate
};

